import type { AnswerMap, JsonValue } from './types';
import { uppercaseName } from './names';

export type FieldType =
	'text' | 'email' | 'phone' | 'date' | 'textarea' | 'address' | 'yes_no' | 'single' | 'multi' | 'currency' | 'repeatable';

export interface DisplayCondition {
	field?: string;
	equals?: JsonValue;
	rule?: 'married' | 'has_minor_child';
}

export interface QuestionnaireField {
	id: string;
	label: string;
	type: FieldType;
	help?: string;
	priority?: boolean;
	humanName?: boolean;
	personChoice?: boolean;
	legacyOnly?: boolean;
	primaryLabel?: string;
	successorLabel?: string;
	maxItems?: number;
	addLabel?: string;
	options?: Array<{ value: string; label: string }>;
	visibleWhen?: DisplayCondition;
	children?: QuestionnaireField[];
}

export interface QuestionnaireSection {
	id: string;
	title: string;
	intro?: string;
	optional?: boolean;
	fields: QuestionnaireField[];
}

const yesNo = [
	{ value: 'yes', label: 'Yes' },
	{ value: 'no', label: 'No' },
];

export const EXECUTOR_EXPLANATION =
	'The primary duties of your executor or other personal representative will be to collect your assets, pay your creditors, and distribute the remaining assets to your heirs or other beneficiaries. The executor also will have to file various types of tax returns and make important choices on them. Therefore it is imperative that you choose someone who is both trustworthy and competent to serve as your executor or personal representative.';

export function isAgentField(id: string): boolean {
	return /^planning\.(executors|poa|health_proxy)\./u.test(id);
}

export function agentsSkipped(answers: AnswerMap): boolean {
	return answers['planning.agents.preference'] === 'skip';
}

export const questionnaireSections: QuestionnaireSection[] = [
	{
		id: 'purpose',
		title: 'Purpose of your consultation',
		intro: 'Choose any reasons that apply. You may leave this blank and discuss it with the attorney.',
		fields: [
			{ id: 'questionnaire.date', label: 'Questionnaire date', type: 'date' },
			{
				id: 'purpose.selections',
				label: 'What would you like help with?',
				type: 'multi',
				priority: true,
				options: [
					{ value: 'medicaid_planning', label: 'Medicaid planning' },
					{ value: 'inheritance', label: 'Inheritance' },
					{ value: 'tax_planning', label: 'Tax planning' },
					{ value: 'creditor_protection', label: 'Creditor protection' },
					{ value: 'avoid_probate', label: 'Avoid probate' },
				],
			},
		],
	},
	{
		id: 'client',
		title: 'About you',
		fields: [
			{ id: 'client.full_name', label: 'Full legal name', type: 'text', humanName: true, priority: true },
			{ id: 'client.dob', label: 'Date of birth', type: 'date', priority: true },
			{ id: 'client.citizenship', label: 'Citizenship', type: 'text', priority: true },
			{ id: 'client.address', label: 'Home address', type: 'address', priority: true },
			{ id: 'client.home_phone', label: 'Home phone', type: 'phone' },
			{ id: 'client.mobile', label: 'Mobile phone', type: 'phone', priority: true },
			{ id: 'client.best_daytime_phone', label: 'Best daytime phone', type: 'phone' },
			{ id: 'client.email', label: 'Email', type: 'email', priority: true },
			{
				id: 'family.marital_status',
				label: 'Marital status',
				type: 'single',
				priority: true,
				options: [
					{ value: 'single', label: 'Single' },
					{ value: 'married', label: 'Married' },
					{ value: 'separated', label: 'Separated' },
					{ value: 'divorced', label: 'Divorced' },
					{ value: 'widowed', label: 'Widowed' },
				],
			},
			{
				id: 'spouse.full_name',
				label: "Spouse's full legal name",
				type: 'text',
				humanName: true,
				priority: true,
				visibleWhen: { rule: 'married' },
			},
			{ id: 'spouse.dob', label: "Spouse's date of birth", type: 'date', priority: true, visibleWhen: { rule: 'married' } },
			{ id: 'spouse.citizenship', label: "Spouse's citizenship", type: 'text', priority: true, visibleWhen: { rule: 'married' } },
			{ id: 'spouse.mobile', label: "Spouse's mobile phone", type: 'phone', visibleWhen: { rule: 'married' } },
			{ id: 'spouse.email', label: "Spouse's email", type: 'email', visibleWhen: { rule: 'married' } },
			{
				id: 'spouse.same_address',
				label: "Is your spouse's address the same as yours?",
				type: 'yes_no',
				options: yesNo,
				visibleWhen: { rule: 'married' },
			},
			{ id: 'spouse.address', label: "Spouse's home address (if different)", type: 'address', visibleWhen: { rule: 'married' } },
		],
	},
	{
		id: 'family',
		title: 'Family',
		fields: [
			{
				id: 'family.children',
				label: 'Children',
				type: 'repeatable',
				help: 'Add each child. Adult children may include contact details.',
				children: [
					{ id: 'full_name', label: 'Full name', type: 'text', humanName: true },
					{ id: 'dob', label: 'Date of birth', type: 'date' },
					{
						id: 'marital_status',
						label: 'Marital status',
						type: 'single',
						options: [
							{ value: 'single', label: 'Single' },
							{ value: 'married', label: 'Married' },
							{ value: 'separated', label: 'Separated' },
							{ value: 'divorced', label: 'Divorced' },
						],
					},
					{ id: 'prior_marriage_parent', label: "If from a previous marriage, other parent's name", type: 'text', humanName: true },
					{ id: 'address', label: 'Address, if adult', type: 'address' },
					{ id: 'phone', label: 'Phone, if adult', type: 'phone' },
					{ id: 'email', label: 'Email, if adult', type: 'email' },
					{
						id: 'spouse_name',
						label: "Name of child's spouse, if applicable",
						type: 'text',
						humanName: true,
						visibleWhen: { field: 'marital_status', equals: 'married' },
					},
				],
			},
			{
				id: 'family.has_special_needs_member',
				label: 'Does anyone in your family have special needs?',
				type: 'yes_no',
				options: yesNo,
				priority: true,
			},
			{
				id: 'family.special_needs_member_name',
				label: 'Who in your family has special needs?',
				type: 'text',
				humanName: true,
				priority: true,
				visibleWhen: { field: 'family.has_special_needs_member', equals: 'yes' },
			},
			{ id: 'family.has_previous_marriage', label: 'Have you been married before?', type: 'yes_no', options: yesNo },
			{
				id: 'family.previous_spouse_names',
				label: 'Previous spouses',
				addLabel: 'Add previous spouse name',
				type: 'repeatable',
				visibleWhen: { field: 'family.has_previous_marriage', equals: 'yes' },
				children: [
					{ id: 'full_name', label: "Previous spouse's name", type: 'text', humanName: true },
					{ id: 'termination_date', label: 'Date of marriage termination', type: 'date' },
					{
						id: 'termination_reason',
						label: 'Marriage terminated by',
						type: 'single',
						options: [
							{ value: 'divorce', label: 'Divorce' },
							{ value: 'death', label: 'Death' },
						],
					},
				],
			},
			{
				id: 'family.has_premarital_agreement',
				label: 'Do you have a premarital/postnuptial agreement?',
				type: 'yes_no',
				options: yesNo,
				priority: true,
				help: 'Please provide a copy, if available.',
			},
			{
				id: 'family.parents',
				label: 'Parents',
				type: 'repeatable',
				children: [
					{ id: 'full_name', label: 'Name', type: 'text', humanName: true },
					{ id: 'age', label: 'Age', type: 'text' },
				],
			},
			{
				id: 'family.siblings',
				label: 'Siblings',
				type: 'repeatable',
				children: [
					{ id: 'full_name', label: 'Name', type: 'text', humanName: true },
					{ id: 'age', label: 'Age', type: 'text' },
				],
			},
		],
	},
	{
		id: 'assets',
		title: 'Assets and income',
		intro: 'Estimated values are fine. Add as many items as you need.',
		fields: [
			{
				id: 'assets.property',
				label: 'Real estate',
				type: 'repeatable',
				children: [
					{ id: 'description', label: 'Description', type: 'text' },
					{ id: 'current_value', label: 'Estimated current value', type: 'currency' },
					{ id: 'purchase_price', label: 'Purchase price', type: 'currency' },
					{ id: 'mortgage', label: 'Outstanding mortgage balance', type: 'currency' },
					{ id: 'ownership', label: 'Who owns it: list name(s) on the deed/title', type: 'text', humanName: true },
					{ id: 'state_address', label: 'Address of the property', type: 'address' },
				],
			},
			{
				id: 'assets.bank_investment',
				label: 'Bank accounts and investments',
				type: 'repeatable',
				children: [
					{ id: 'account_type_or_institution', label: 'Previously entered type or institution (retained)', type: 'text', legacyOnly: true },
					{ id: 'institution', label: 'Name of institution', type: 'text' },
					{ id: 'account_type', label: 'Account type', type: 'text' },
					{ id: 'estimated_value', label: 'Estimated value of the account', type: 'currency' },
					{ id: 'owner', label: 'Owner of the account', type: 'text', humanName: true },
				],
			},
			{
				id: 'assets.retirement',
				label: 'Retirement accounts',
				type: 'repeatable',
				children: [
					{ id: 'account_type', label: 'Account type', type: 'text' },
					{ id: 'institution', label: 'Institution', type: 'text' },
					{ id: 'estimated_value', label: 'Estimated value', type: 'currency' },
					{ id: 'owner', label: 'Owner', type: 'text', humanName: true },
					{ id: 'beneficiary', label: 'Beneficiary', type: 'text', humanName: true },
				],
			},
			{
				id: 'assets.business',
				label: 'Business assets',
				type: 'repeatable',
				help: 'Enter the value of the entire business and this owner’s percentage. If your spouse also owns a share, add a separate entry for that owner and percentage.',
				children: [
					{ id: 'business_name', label: 'Business name', type: 'text' },
					{ id: 'estimated_value', label: 'Estimated value of the entire business', type: 'currency' },
					{ id: 'owner', label: 'Owner', type: 'text', personChoice: true },
					{ id: 'ownership_percentage', label: 'Percentage of ownership', type: 'text' },
					{ id: 'business_type', label: 'Business type', type: 'text' },
					{
						id: 'tax_return_type',
						label: 'Type of tax return filed',
						type: 'text',
						help: 'State the form name: Sch C, 1120, 1120S, 1065.',
					},
				],
			},
			{
				id: 'assets.social_security_pension',
				label: 'Social Security and pension income',
				type: 'repeatable',
				help: 'Enter gross income before deductions. Choose Monthly or Annual for each amount. Monthly equivalents of annual amounts are averages.',
				children: [
					{ id: 'source', label: 'Source of income: Social Security, pension, union, RMD', type: 'text' },
					{ id: 'annual_amount', label: 'Previously entered annual amount (retained)', type: 'currency', legacyOnly: true },
					{ id: 'amount', label: 'Gross income amount', type: 'currency' },
					{
						id: 'frequency',
						label: 'Amount is',
						type: 'single',
						options: [
							{ value: 'monthly', label: 'Monthly' },
							{ value: 'annual', label: 'Annual' },
						],
					},
					{ id: 'owner', label: 'Owner', type: 'text', personChoice: true },
				],
			},
			{ id: 'assets.has_safe_deposit_box', label: 'Do you have a safe deposit box?', type: 'yes_no', options: yesNo, priority: true },
			{
				id: 'assets.safe_deposit_box_bank',
				label: 'Name(s) of the bank(s)',
				type: 'text',
				priority: true,
				visibleWhen: { field: 'assets.has_safe_deposit_box', equals: 'yes' },
			},
		],
	},
	{
		id: 'liabilities_insurance',
		title: 'Liabilities/debts, insurance, and advisers',
		fields: [
			{
				id: 'liabilities.items',
				label: 'Liabilities/debts',
				type: 'repeatable',
				children: [
					{ id: 'creditor', label: 'Name of creditor/type of debt', type: 'text' },
					{ id: 'amount_owed', label: 'Estimated amount of debt', type: 'currency' },
				],
			},
			{ id: 'insurance.has_life_insurance', label: 'Do you have life insurance policies?', type: 'yes_no', options: yesNo, priority: true },
			{
				id: 'insurance.policies',
				label: 'Life insurance policies',
				type: 'repeatable',
				visibleWhen: { field: 'insurance.has_life_insurance', equals: 'yes' },
				children: [
					{ id: 'insured', label: 'Name of insured', type: 'text', personChoice: true },
					{ id: 'face_value', label: 'Face value of the policies / death benefits', type: 'currency' },
					{ id: 'current_beneficiary', label: 'Names of beneficiaries', type: 'text', humanName: true },
					{ id: 'institution_or_broker', label: 'Financial institution or broker', type: 'text' },
				],
			},
			{ id: 'advisors.financial_advisor', label: 'Name of your financial advisor and their contact information', type: 'textarea' },
			{
				id: 'advisors.wants_portfolio_review',
				label: 'Would you like a financial adviser to review your portfolio?',
				type: 'yes_no',
				options: yesNo,
			},
			{ id: 'intake.referral_source', label: 'Who recommended us to you?', type: 'text', humanName: true },
		],
	},
	{
		id: 'fiduciaries',
		title: 'Appointment of agents',
		intro:
			'Appointment of agents will be discussed with our attorneys during your consultation, but if you wish to list those agents in this intake, please proceed with this section.',
		fields: [
			{
				id: 'planning.agents.preference',
				label: 'Would you like to list your executors, powers of attorney, and health care proxies now?',
				type: 'single',
				options: [
					{ value: 'proceed', label: 'Proceed' },
					{ value: 'skip', label: 'Skip for now' },
				],
				help: 'Skip hides only these agent questions and leaves their PDF spaces blank. Any answers already saved are kept if you choose Proceed again. Specific legacies, trustees, beneficiaries, and guardians remain available.',
			},
			{
				id: 'planning.executors.client',
				label: 'Your executor and successor executor',
				primaryLabel: 'Primary executor',
				successorLabel: 'Successor executor',
				help: EXECUTOR_EXPLANATION,
				type: 'repeatable',
				children: [
					{ id: 'full_name', label: 'Name', type: 'text', humanName: true },
					{ id: 'address', label: 'Address', type: 'address' },
				],
			},
			{
				id: 'planning.executors.spouse',
				label: "Your spouse's executor and successor executor",
				primaryLabel: 'Primary executor',
				successorLabel: 'Successor executor',
				type: 'repeatable',
				visibleWhen: { rule: 'married' },
				children: [
					{ id: 'full_name', label: 'Name', type: 'text', humanName: true },
					{ id: 'address', label: 'Address', type: 'address' },
				],
			},
			{
				id: 'planning.legacies',
				label: 'Specific legacies',
				type: 'repeatable',
				children: [
					{ id: 'beneficiary', label: 'Beneficiary', type: 'text', humanName: true },
					{ id: 'amount_or_property', label: 'Amount or description of property', type: 'text' },
				],
			},
			{
				id: 'planning.poa.client',
				maxItems: 3,
				label: 'Your power of attorney and successor',
				primaryLabel: 'Primary power of attorney',
				successorLabel: 'Successor power of attorney',
				type: 'repeatable',
				children: [
					{ id: 'full_name', label: 'Name', type: 'text', humanName: true },
					{ id: 'address', label: 'Address', type: 'address' },
				],
			},
			{
				id: 'planning.poa.client.effective_when',
				label: 'When should your power of attorney take effect?',
				type: 'single',
				options: [
					{ value: 'immediate', label: 'Immediately upon signing' },
					{ value: 'future_incapacity', label: 'Upon future incapacity' },
				],
			},
			{
				id: 'planning.poa.client.incapacity_determiner',
				label: 'Who should determine incapacity?',
				type: 'text',
				humanName: true,
				visibleWhen: { field: 'planning.poa.client.effective_when', equals: 'future_incapacity' },
			},
			{
				id: 'planning.poa.spouse',
				maxItems: 3,
				label: "Your spouse's power of attorney and successor",
				primaryLabel: 'Primary power of attorney',
				successorLabel: 'Successor power of attorney',
				type: 'repeatable',
				visibleWhen: { rule: 'married' },
				children: [
					{ id: 'full_name', label: 'Name', type: 'text', humanName: true },
					{ id: 'address', label: 'Address', type: 'address' },
				],
			},
			{
				id: 'planning.health_proxy.client',
				maxItems: 3,
				label: 'Your health care proxy and successor',
				primaryLabel: 'Primary health care proxy',
				successorLabel: 'Successor health care proxy',
				type: 'repeatable',
				children: [
					{ id: 'full_name', label: 'Name', type: 'text', humanName: true },
					{ id: 'phone', label: 'Phone', type: 'phone' },
					{ id: 'address', label: 'Address', type: 'address' },
				],
			},
			{
				id: 'planning.health_proxy.spouse',
				maxItems: 3,
				label: "Your spouse's health care proxy and successor",
				primaryLabel: 'Primary health care proxy',
				successorLabel: 'Successor health care proxy',
				type: 'repeatable',
				visibleWhen: { rule: 'married' },
				children: [
					{ id: 'full_name', label: 'Name', type: 'text', humanName: true },
					{ id: 'phone', label: 'Phone', type: 'phone' },
					{ id: 'address', label: 'Address', type: 'address' },
				],
			},
		],
	},
	{
		id: 'personal_directions',
		title: 'Personal directions',
		fields: [
			{
				id: 'planning.trustees',
				label: 'Client-proposed trustees',
				type: 'repeatable',
				primaryLabel: 'Primary trustee',
				successorLabel: 'Successor trustee',
				help: 'List your proposed choices for one trust. Your attorney will discuss these with you during consultation.',
				children: [
					{ id: 'full_name', label: 'Name', type: 'text', humanName: true },
					{ id: 'address', label: 'Address', type: 'address' },
				],
			},
			{
				id: 'planning.trust_beneficiaries',
				label: 'Who are your primary beneficiaries?',
				type: 'repeatable',
				addLabel: 'Add primary beneficiary',
				help: 'List the proposed primary beneficiaries of your trust.',
				children: [{ id: 'full_name', label: 'Beneficiary name', type: 'text', humanName: true }],
			},
			{ id: 'planning.living_will.client', label: 'Living Will', type: 'textarea' },
			{
				id: 'planning.living_will.spouse',
				label: "Your spouse's living-will preferences",
				type: 'textarea',
				visibleWhen: { rule: 'married' },
			},
			{
				id: 'planning.digital_assets.allow_access',
				label: 'Would you like your agent to have access to your digital assets:',
				type: 'yes_no',
				options: yesNo,
				help: 'digital pictures (websites where you store your digital pictures), e-mail accounts, mobile phone, social media accounts, website domains, PayPal, eBay, miles, etc. Do not provide passwords or access codes.',
			},
			{
				id: 'planning.digital_assets.items',
				label: 'Provide a list of your digital assets.',
				type: 'repeatable',
				addLabel: 'Add digital asset',
				visibleWhen: { field: 'planning.digital_assets.allow_access', equals: 'yes' },
				children: [{ id: 'description', label: 'Name or type of digital asset', type: 'text' }],
			},
			{
				id: 'planning.guardians',
				label: 'Primary guardian and successor guardians',
				primaryLabel: 'Primary guardian',
				successorLabel: 'Successor guardian',
				type: 'repeatable',
				priority: true,
				visibleWhen: { rule: 'has_minor_child' },
				children: [
					{ id: 'full_name', label: 'Name', type: 'text', humanName: true },
					{ id: 'relationship', label: 'Relationship to you', type: 'text' },
					{ id: 'phone', label: 'Phone', type: 'phone' },
					{ id: 'address', label: 'Address', type: 'address' },
				],
			},
		],
	},
];

export const allFields = questionnaireSections.flatMap((section) => section.fields);

export function normalizeNameAnswer(field: QuestionnaireField, value: JsonValue): JsonValue {
	if (field.personChoice && typeof value === 'string') return value.startsWith('person:') ? value : uppercaseName(value);
	if (field.humanName && typeof value === 'string') return uppercaseName(value);
	if (field.type !== 'repeatable' || !Array.isArray(value)) return value;
	return value.map((item) => {
		if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
		const next = { ...item };
		for (const child of field.children ?? []) {
			if (Object.hasOwn(next, child.id)) next[child.id] = normalizeNameAnswer(child, next[child.id]);
		}
		return next;
	});
}

export function isBlank(value: JsonValue | undefined): boolean {
	if (value === undefined || value === null || value === '') return true;
	if (Array.isArray(value)) {
		return value.length === 0 || value.every((item) => isBlank(item));
	}
	if (typeof value === 'object') {
		return Object.values(value).every((item) => isBlank(item));
	}
	return false;
}

function hasMinorChild(answers: AnswerMap): boolean {
	const children = answers['family.children'];
	if (!Array.isArray(children)) return false;
	const today = new Date();
	return children.some((child) => {
		if (!child || typeof child !== 'object' || Array.isArray(child)) return false;
		const dob = child.dob;
		if (typeof dob !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dob)) return false;
		const birth = new Date(`${dob}T00:00:00Z`);
		if (Number.isNaN(birth.getTime()) || birth > today) return false;
		let age = today.getUTCFullYear() - birth.getUTCFullYear();
		const monthDifference = today.getUTCMonth() - birth.getUTCMonth();
		if (monthDifference < 0 || (monthDifference === 0 && today.getUTCDate() < birth.getUTCDate())) age -= 1;
		return age < 18;
	});
}

export function isFieldVisible(field: QuestionnaireField, answers: AnswerMap): boolean {
	if (field.id === 'spouse.address' && answers['spouse.same_address'] === 'yes') return false;
	if (isAgentField(field.id) && agentsSkipped(answers)) return false;
	if (
		isAgentField(field.id) &&
		answers['planning.agents.preference'] !== 'proceed' &&
		!Object.entries(answers).some(([id, value]) => isAgentField(id) && !isBlank(value))
	)
		return false;
	if (field.legacyOnly && isBlank(answers[field.id])) return false;
	const condition = field.visibleWhen;
	if (!condition) return true;
	if (condition.rule === 'married') return answers['family.marital_status'] === 'married';
	if (condition.rule === 'has_minor_child') return hasMinorChild(answers);
	return condition.field ? answers[condition.field] === condition.equals : true;
}

export function missingPriorityFields(answers: AnswerMap): QuestionnaireField[] {
	return allFields.filter((field) => field.priority && isFieldVisible(field, answers) && isBlank(answers[field.id]));
}

export const allowedFieldIds = new Set(allFields.map((field) => field.id));
